import React from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import CoursesTable from "./CoursesTable";
import { PlusCircle } from "../../../components/svgIcons";
import {
  useGetCoursesBySchoolByQueryQuery,
  useAssignCourseToFacultyMutation,
  useCreateCoursePlaceholderMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useDeleteCourseCascadeMutation,
  GetCoursesBySchoolByQueryDocument,
  CreateCoursePlaceholderMutation,
  useGenerateCourseContentMutation,
  useCloneCourseMutation,
  useEditCourseMutation,
  GetAvailableCoursesForStudentDocument,
  GetEnrolledCoursesForStudentDocument,
  StoragePathType,
  Course,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import AssignFacultyToCourseListContainer from "./AssignFacultyToCourseListContainer";
import RemoveFacultyFromCourseListContainer from "./RemoveFacultyFromCourseListContainer";
import AddCourseModal from "./AddCourseModalContainer";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import RosterManagementContainer from "../../courseSettings/RosterManagementContainer/RosterManagementContainer";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CourseDegreesTabContainer from "../CourseDegreesTabContainer/CourseDegreesTabContainer";
import BulkUploadCourseModalContainer from "./BulkUploadCourseModalContainer";
import EditCourseModalContainer from "../CoursesTabContainer/EditCourseModalContainer";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import usePagination from "../../../hooks/usePagination";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import { useAppConfig } from "../../../AppConfigProvider";

const modals = {
  removeTeacher: {
    title: "Remove Faculty from Course?",
    ariaLabel: "Remove Faculty From Course Modal",
    warningText:
      "Following faculty will no longer have access to the course. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove from Course",
  },
  deleteCourse: {
    title: "Delete Course?",
    ariaLabel: "Delete Course Modal",
    warningText:
      "Are you sure you want to permanently delete this course? This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Delete Course",
  },
  deleteConfirmation: {
    title: "Course Deleted",
    ariaLabel: "Course Deleted Modal",
    warningText: "The course has been successfully deleted.",
    actionButtonText: "OK",
  },
  imageUpdateConfirmation: {
    title: "Success!",
    ariaLabel: "Course Image Updated Successfully Modal",
    warningText: "The course image has been successfully updated.",
    actionButtonText: "OK",
  },
  imageUpdateError: {
    title: "Error!",
    ariaLabel: "Course Image Update Error Modal",
    warningText: "An error occurred while updating the course image.",
    actionButtonText: "CLOSE",
  },
  cloneCourse: {
    title: "Clone Course?",
    ariaLabel: "Clone Course Modal",
    warningText: "Are you sure you want to clone this course?",
    actionButtonText: "Clone Course",
  },
  processingCloneCourse: {
    title: "Processing Clone Course...",
    ariaLabel: "Processing Clone Course Modal",
    warningText: "Please wait while we clone the course.",
    actionButtonText: "CLOSE",
  },
  cloneCourseConfirmation: {
    title: "Course Cloned",
    ariaLabel: "Course Cloned Modal",
    warningText: "The course has been successfully cloned.",
    actionButtonText: "OK",
  },
};

export enum CoursesTabModalState {
  CLOSED = "CLOSED",
  DELETE_COURSE = "DELETE_COURSE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  ASSIGN_FACULTY = "ASSIGN_FACULTY",
  REMOVE_FACULTY = "REMOVE_FACULTY",
  ADD_COURSE = "ADD_COURSE",
  COURSE_ROSTER = "COURSE_ROSTER",
  COURSE_DEGREES = "COURSE_DEGREES",
  EDIT_IMAGE = "EDIT_IMAGE",
  EDIT_COURSE = "EDIT_COURSE",
  IMAGE_UPDATE_CONFIRMATION = "IMAGE_UPDATE_CONFIRMATION",
  IMAGE_UPDATE_ERROR = "IMAGE_UPDATE_ERROR",
  GENERATE_COURSE = "GENERATE_COURSE",
  BULK_UPLOAD_COURSE = "BULK_UPLOAD_COURSE",
  CLONE_COURSE = "CLONE_COURSE",
  CLONE_COURSE_CONFIRMATION = "CLONE_COURSE_CONFIRMATION",
  PROCESSING_CLONE_COURSE = "PROCESSING_CLONE_COURSE",
}

const CoursesTabContainer = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = useState({
    state: CoursesTabModalState.CLOSED,
    courseId: "",
    courseName: "",
    isCourseProcessing: false,
  });

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const [editCourse] = useEditCourseMutation();

  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const { page, setPage, pageSize, setPageSize } = usePagination({});

  const { data: coursesData, refetch: coursesRefetch } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId,
      searchQuery,
      page,
      pageSize,
      sortBy,
    },
  });

  const totalItemsCount = coursesData?.getCoursesBySchoolByQuery?.data.totalCount || 0;

  const refetchQueries = [
    {
      query: GetCoursesBySchoolByQueryDocument,
      variables: {
        schoolId,
        searchQuery,
        page,
        pageSize,
        sortBy,
      },
    },
    {
      query: GetAvailableCoursesForStudentDocument,
      variables: {
        schoolId,
      },
    },
    {
      query: GetEnrolledCoursesForStudentDocument,
      variables: {
        schoolId,
      },
    },
  ];

  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [createCoursePlaceholder] = useCreateCoursePlaceholderMutation();
  const [deleteCourseCascade] = useDeleteCourseCascadeMutation();
  const [generateCourseContent] = useGenerateCourseContentMutation();

  const [cloneCourse] = useCloneCourseMutation();

  const handleEditCourse = async ({
    courseName,
    courseCode,
    description,
    objectives,
    cost,
    targetAudience,
    thumbnail,
  }: {
    courseName?: string;
    courseCode?: string;
    description?: string;
    objectives?: string;
    cost?: number;
    targetAudience?: string;
    thumbnail?: string;
  }): Promise<{ success: boolean }> => {
    if (modalState.isCourseProcessing) {
      return {
        success: false,
      };
    }

    try {
      const response = await editCourse({
        variables: {
          id: modalState.courseId,
          course: {
            name: courseName,
            course_code: courseCode,
            objectives: objectives?.split("\n").filter((line) => line.trim() !== "") || [],
            description,
            cost,
            ...(targetAudience && { targetAudience }),
            ...(thumbnail && { thumbnail }),
          },
        },
        refetchQueries,
      });

      const result = response.data?.editCourse;

      return {
        success: !!result?.success,
      };
    } catch (error) {
      console.error("Error editing course:", error);
      return {
        success: false,
      };
    }
  };

  const addCourse = async ({
    courseName,
    courseCode,
    degrees,
    description,
    objectives,
    targetAudience,
    cost,
  }: {
    courseName?: string;
    courseCode?: string;
    degrees?: string[];
    description?: string;
    objectives?: string;
    targetAudience?: string;
    cost?: number;
  }) => {
    try {
      const result = await createCoursePlaceholder({
        variables: {
          name: courseName,
          courseCode,
          schoolId,
          description,
          objectives,
          targetAudience,
          cost,
          degreeIds: degrees,
        },
        refetchQueries: refetchQueries,
      });

      return result.data;
    } catch (error) {
      return {
        createCoursePlaceholder: {
          data: null,
          success: false,
        },
      } as unknown as CreateCoursePlaceholderMutation;
    }
  };

  const assignFaculty = async (facultyId: string) => {
    await assignCourseToFaculty({
      variables: {
        courseId: modalState.courseId,
        userId: facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "", isCourseProcessing: false });
  };

  const removeFaculty = async (facultyId: string) => {
    await removeCourseFromFaculty({
      variables: {
        courseId: modalState.courseId,
        userId: facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "", isCourseProcessing: false });
  };

  const handleDeleteCourse = async () => {
    if (modalState.isCourseProcessing) {
      return;
    }

    await deleteCourseCascade({
      variables: {
        id: modalState.courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({
      state: CoursesTabModalState.DELETE_CONFIRMATION,
      courseId: "",
      courseName: "",
      isCourseProcessing: false,
    });
    setSearchQuery("");
  };

  const handleCloneCourse = async () => {
    if (modalState.isCourseProcessing) {
      return;
    }

    setModalState({
      state: CoursesTabModalState.PROCESSING_CLONE_COURSE,
      courseId: "",
      courseName: "",
      isCourseProcessing: true,
    });
    await cloneCourse({
      variables: { courseId: modalState.courseId },
      refetchQueries: refetchQueries,
    });
    setModalState({
      state: CoursesTabModalState.CLONE_COURSE_CONFIRMATION,
      courseId: "",
      courseName: "",
      isCourseProcessing: false,
    });
    setSearchQuery("");
  };

  const handleActionModalOpen = async (
    courseId: string,
    courseName: string,
    modalType: CoursesTabModalState,
    isCourseProcessing: boolean
  ) => {
    setModalState({
      state: modalType,
      courseId,
      courseName,
      isCourseProcessing: isCourseProcessing,
    });
  };

  const handleModalClose = () => {
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "", isCourseProcessing: false });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    coursesRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleImageUpload = async (thumbnail: string) => {
    setIsSubmitting(true);
    try {
      const response = await editCourse({
        variables: {
          id: modalState.courseId,
          course: {
            thumbnail,
          },
        },
      });

      if (response.data?.editCourse?.success) {
        setModalState((prev) => ({
          ...prev,
          state: CoursesTabModalState.IMAGE_UPDATE_CONFIRMATION,
        }));
      } else {
        setModalState((prev) => ({
          ...prev,
          state: CoursesTabModalState.IMAGE_UPDATE_ERROR,
        }));
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      setModalState((prev) => ({
        ...prev,
        state: CoursesTabModalState.IMAGE_UPDATE_ERROR,
      }));
      setIsSubmitting(false);
    }
  };

  if (modalState.state === CoursesTabModalState.COURSE_ROSTER) {
    return (
      <RosterManagementContainer
        courseId={modalState.courseId}
        onBack={() => setModalState((prev) => ({ ...prev, state: CoursesTabModalState.CLOSED }))}
        onRefetch={() => coursesRefetch()}
      />
    );
  }

  if (modalState.state === CoursesTabModalState.COURSE_DEGREES) {
    return (
      <CourseDegreesTabContainer
        course={coursesData?.getCoursesBySchoolByQuery?.data.courses.find(
          (course) => course.id === modalState.courseId
        )}
        onBack={() => setModalState((prev) => ({ ...prev, state: CoursesTabModalState.CLOSED }))}
        refetchQueryVariables={{
          searchQueryProp: searchQuery,
          page,
          pageSize,
          sortBy,
        }}
      />
    );
  }

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <CustomText text="Manage Courses" size="l" weight="bold" style={styles.headerText} />
            <View style={styles.buttonGroup}>
              <NewCustomActionButton
                text="Add a Course"
                onPress={() =>
                  setModalState({
                    state: CoursesTabModalState.ADD_COURSE,
                    courseId: "",
                    courseName: "",
                    isCourseProcessing: false,
                  })
                }
                leftIcon={<PlusCircle />}
              />
              <NewCustomActionButton
                text="Bulk Upload"
                onPress={() =>
                  setModalState({
                    state: CoursesTabModalState.BULK_UPLOAD_COURSE,
                    courseId: "",
                    courseName: "",
                    isCourseProcessing: false,
                  })
                }
                leftIcon={<PlusCircle />}
              />
            </View>
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Courses" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${coursesData?.getCoursesBySchoolByQuery.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <CoursesTable
          courses={coursesData?.getCoursesBySchoolByQuery?.data.courses || []}
          onActionModalOpen={handleActionModalOpen}
        />
        {totalItemsCount > 0 && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.DELETE_COURSE}
        onClose={handleModalClose}
        onAction={handleDeleteCourse}
        modalTexts={modals.deleteCourse}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.DELETE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.deleteConfirmation}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.CLONE_COURSE}
        onClose={handleModalClose}
        onAction={handleCloneCourse}
        modalTexts={modals.cloneCourse}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.PROCESSING_CLONE_COURSE}
        onClose={handleModalClose}
        modalTexts={modals.processingCloneCourse}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.CLONE_COURSE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.cloneCourseConfirmation}
        contextualText={modalState.courseName}
      />
      <AssignFacultyToCourseListContainer
        visible={modalState.state === CoursesTabModalState.ASSIGN_FACULTY}
        onClose={handleModalClose}
        onAction={assignFaculty}
      />
      <RemoveFacultyFromCourseListContainer
        visible={modalState.state === CoursesTabModalState.REMOVE_FACULTY}
        onClose={handleModalClose}
        onAction={removeFaculty}
        courseId={modalState.courseId}
      />
      <AddCourseModal
        visible={modalState.state === CoursesTabModalState.ADD_COURSE}
        onClose={handleModalClose}
        onSubmit={addCourse}
      />
      <EditCourseModalContainer
        visible={modalState.state === CoursesTabModalState.EDIT_COURSE}
        onClose={handleModalClose}
        onSubmit={handleEditCourse}
        courseData={
          coursesData?.getCoursesBySchoolByQuery?.data?.courses?.find(
            (course) => course?.id === modalState.courseId
          ) as Course
        }
      />

      {/* Add this new modal condition */}
      {modalState.state === CoursesTabModalState.EDIT_IMAGE && (
        <CustomModal
          visible={true}
          onRequestClose={handleModalClose}
          title="Edit Course Image"
          ariaLabel="Edit Course Image Modal"
          showCloseButton={true}
          style={{ width: "90%" }}
          exitFill={undefined}
          onShow={() => {}}>
          <View style={styles.modalContent}>
            <ImageUpload
              onChange={handleImageUpload}
              storagePath={StoragePathType.Thumbnails}
              aspectRatio={[16, 9]}
              buttonText="Upload Course Image"
              changeButtonText="Change Course Image"
              dropzoneText="Drag & drop course image here"
              dropzoneSubtext="Recommended: 1792x1024px. JPG or PNG format"
            />
            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleModalClose}
                styleType="secondary"
                size="m"
                style={isSubmitting ? { backgroundColor: "#ccc" } : styles.cancelButton}
                textStyle={styles.cancelButtonText}
                disabled={isSubmitting}
              />
            </View>
          </View>
        </CustomModal>
      )}
      {/* Add these new modals */}
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.IMAGE_UPDATE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.imageUpdateConfirmation}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.IMAGE_UPDATE_ERROR}
        onClose={handleModalClose}
        modalTexts={modals.imageUpdateError}
        contextualText={modalState.courseName}
      />
      {modalState.state === CoursesTabModalState.BULK_UPLOAD_COURSE && (
        <BulkUploadCourseModalContainer
          visible={true}
          onClose={handleModalClose}
          onSubmit={async (formData) => {
            return await addCourse(formData);
          }}
          onGenerateCourse={async (courseId, numberOfModules, numberOfActivities) => {
            await generateCourseContent({
              variables: {
                courseId,
                numberOfModules,
                numberOfActivities,
              },
            });
          }}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  buttonGroup: {
    flexDirection: "row",
    gap: 12,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  modalContent: {
    padding: 24,
    width: "100%",
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },

  cancelButtonText: {
    color: "#4B5563",
  },
});

export default CoursesTabContainer;
